const L = (a, b, t) => a + (b - a) * t;
const LP = (a, b, t) => [L(a[0], b[0], t), L(a[1], b[1], t)];
const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const mix = (a, b, t) => {
  const [x, y] = [hex(a), hex(b)];
  return `rgb(${x.map((v, i) => Math.round(L(v, y[i], t))).join(",")})`;
};
const bez = (P, t) => {
  const u = 1 - t;
  const k = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
  return [0, 1].map((j) => P.reduce((s, p, i) => s + k[i] * p[j], 0));
};
const bezD = (P, t) => {
  const u = 1 - t;
  return [0, 1].map((j) => 3 * u * u * (P[1][j] - P[0][j]) + 6 * u * t * (P[2][j] - P[1][j]) + 3 * t * t * (P[3][j] - P[2][j]));
};
const pt = (p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
const star = (x, y, s) => `M${x} ${y - s} L${x + s * 0.3} ${y - s * 0.3} L${x + s} ${y} L${x + s * 0.3} ${y + s * 0.3} L${x} ${y + s} L${x - s * 0.3} ${y + s * 0.3} L${x - s} ${y} L${x - s * 0.3} ${y - s * 0.3} Z`;
const zigzag = "l8 -8 l2 10 l8 -8";

const SKIN = "#F5C6A5";
const SKIN_DARK = "#E2A47F";
const INK = "#0F172A";

function geometry(e) {
  const hip = LP([146, 252], [150, 240], e);
  const S = [hip, LP([116, 204], [146, 192], e), LP([152, 118], [152, 140], e), LP([206, 146], [156, 106], e)];
  const knee = LP([174, 320], [154, 320], e);
  const ankle = [150, 392];
  const sh = bez(S, 0.84);
  const shoulder = [sh[0] + 8, sh[1]];
  return {
    S, hip, ankle, shoulder, neck: S[3],
    head: LP([234, 136], [166, 66], e),
    elbow: LP([shoulder[0] + 24, shoulder[1] + 62], [shoulder[0] - 30, shoulder[1] + 52], e),
    hand: LP([shoulder[0] + 44, shoulder[1] + 112], [hip[0] + 2, hip[1] - 18], e),
    spine: `M${pt(S[0])} C${pt(S[1])} ${pt(S[2])} ${pt(S[3])}`,
    leg: `M${pt(hip)} L${pt(knee)} L${pt(ankle)}`,
    strain: bez(S, 0.68),
  };
}

const vertebraeAlong = (S) =>
  Array.from({ length: 11 }, (_, i) => {
    const t = 0.08 + i * 0.084;
    const q = bez(S, t);
    const d = bezD(S, t);
    const len = Math.hypot(d[0], d[1]) || 1;
    return { id: `vertebra-${i + 1}`, x: q[0] + (d[1] / len) * 17, y: q[1] + (-d[0] / len) * 17, rot: (Math.atan2(d[1], d[0]) * 180) / Math.PI + 90 };
  });

const Head = ({ c, tilt, e }) => {
  const [x, y] = c;
  return (
    <g transform={`rotate(${tilt.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})`}>
      <circle cx={x} cy={y} r="33" fill={SKIN} />
      <path d={`M${x - 33} ${y + 4} C${x - 36} ${y - 30} ${x + 6} ${y - 46} ${x + 30} ${y - 16} C${x + 12} ${y - 22} ${x - 6} ${y - 18} ${x - 14} ${y + 2} Z`} fill="#1E293B" />
      <ellipse cx={x - 6} cy={y + 4} rx="6" ry="8" fill={SKIN_DARK} />
      <circle cx={x + 14} cy={y - 3} r="3.4" fill={INK} />
      <path d={`M${x + 8} ${y - 11} L${x + 21} ${y - 12 + L(3, -1.5, e)}`} stroke={INK} strokeWidth="2.4" strokeLinecap="round" />
      <path d={`M${x + 31} ${y - 4} q 6 6 -1 9`} stroke={SKIN_DARK} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d={`M${x + 10} ${y + 15} Q${x + 18} ${y + 15 + L(-4, 7, e)} ${x + 26} ${y + 13}`} stroke={INK} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <circle cx={x + 20} cy={y + 7} r="5" fill="#FB7185" opacity={0.35 * e} />
    </g>
  );
};

const Backdrop = ({ e, glow }) => (
  <>
    <defs>
      <radialGradient id="posture-aura" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.28" />
        <stop offset="60%" stopColor="#8CC63F" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#8CC63F" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="160" cy="220" r="165" fill="url(#posture-aura)" opacity={L(0.3, 1, e)} />
    <line x1="150" y1="28" x2="150" y2="396" stroke="#539323" strokeWidth="2" strokeDasharray="5 7" opacity={0.75 * glow} />
    <ellipse cx="160" cy="402" rx="86" ry="9" fill="#0B1B3D" opacity="0.1" />
  </>
);

const Legs = ({ leg, ankle }) => (
  <>
    <path d={leg} transform="translate(-10 0)" stroke="#1E293B" strokeWidth="28" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <ellipse cx={ankle[0] + 4} cy="398" rx="22" ry="8" fill={INK} />
    <path d={leg} stroke="#0B1B3D" strokeWidth="28" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <ellipse cx={ankle[0] + 14} cy="399" rx="22" ry="8" fill={INK} />
  </>
);

const Vertebrae = ({ S, fill }) =>
  vertebraeAlong(S).map((v) => (
    <rect key={v.id} x={v.x - 8} y={v.y - 4.5} width="16" height="9" rx="4" fill={fill} stroke="#ffffff" strokeWidth="1.5" transform={`rotate(${v.rot.toFixed(1)} ${v.x.toFixed(1)} ${v.y.toFixed(1)})`} />
  ));

const Arm = ({ g, color }) => (
  <>
    <path d={`M${pt(g.shoulder)} L${pt(g.elbow)} L${pt(g.hand)}`} stroke={color} strokeWidth="17" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "brightness(0.92)" }} />
    <circle cx={g.hand[0]} cy={g.hand[1]} r="8" fill={SKIN} />
  </>
);

const StrainMarks = ({ g, e }) => (
  <g opacity={1 - e}>
    <path d={`M${g.strain[0] - 46} ${g.strain[1] - 10} ${zigzag}`} stroke="#F97316" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d={`M${g.neck[0] - 30} ${g.neck[1] - 26} l7 -7 l2 9 l7 -7`} stroke="#F97316" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const Sparkles = ({ glow }) => (
  <g fill="#FBBF24" opacity={glow}>
    <path d={star(228, 52, 9)} />
    <path d={star(104, 70, 6)} />
    <path d={star(236, 108, 5)} />
  </g>
);

export const PostureFigure = ({ p, className = "" }) => {
  const e = Math.min(1, Math.max(0, p));
  const glow = Math.max(0, (e - 0.75) / 0.25);
  const g = geometry(e);
  const shirt = mix("#94A3B8", "#1D6FD8", e);
  return (
    <svg viewBox="0 0 320 420" className={className} role="img" aria-label="Posture illustration: a figure moving from a slouched posture to an upright one" data-testid="posture-figure" data-progress={e.toFixed(2)}>
      <Backdrop e={e} glow={glow} />
      <Legs leg={g.leg} ankle={g.ankle} />
      <path d={g.spine} stroke={shirt} strokeWidth="58" fill="none" strokeLinecap="round" />
      <Vertebrae S={g.S} fill={mix("#F97316", "#539323", e)} />
      <path d={`M${pt(g.neck)} L${pt([g.head[0] - 8, g.head[1] + 26])}`} stroke={SKIN} strokeWidth="20" strokeLinecap="round" />
      <Head c={g.head} tilt={L(26, -4, e)} e={e} />
      <Arm g={g} color={shirt} />
      <StrainMarks g={g} e={e} />
      <Sparkles glow={glow} />
    </svg>
  );
};
